import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { 
  Package, Plus, Warehouse, BarChart2, X, Trash2, FileText, 
  ChevronDown, AlertCircle, CheckCircle, Upload, Search, Users,
  ArrowRight, User, Send, ArrowLeftRight, Repeat, RotateCcw, AlertOctagon, Undo2,
  TrendingUp, TrendingDown, Calendar, Download, Filter, RefreshCw, ClipboardList
} from "lucide-react";
import { toast } from "sonner";

// =============================================================================
// ITEM TYPES for inventory
// =============================================================================
const ITEM_TYPES = [
  { value: "meter", label: "Smart Meter", hasSerial: true },
  { value: "smc_box", label: "SMC Box", hasSerial: true },
  { value: "seal", label: "Seal", hasSerial: false },
  { value: "cable", label: "Cable", hasSerial: false },
  { value: "ct_coil", label: "CT Coil", hasSerial: true },
  { value: "modem", label: "Communication Modem", hasSerial: true },
  { value: "antenna", label: "Antenna", hasSerial: false },
  { value: "terminal_block", label: "Terminal Block", hasSerial: false },
  { value: "fuse", label: "Fuse", hasSerial: false },
  { value: "mcb", label: "MCB", hasSerial: false },
  { value: "ladder", label: "Ladder", hasSerial: false },
  { value: "safety_belt", label: "Safety Belt", hasSerial: true },
  { value: "helmet", label: "Safety Helmet", hasSerial: false },
  { value: "gloves", label: "Gloves", hasSerial: false },
  { value: "other", label: "Other", hasSerial: false },
];

const ITEM_COLORS = {
  meter: "bg-blue-100 text-blue-700",
  smc_box: "bg-purple-100 text-purple-700",
  seal: "bg-orange-100 text-orange-700",
  cable: "bg-yellow-100 text-yellow-700",
  ct_coil: "bg-cyan-100 text-cyan-700",
  modem: "bg-pink-100 text-pink-700",
  antenna: "bg-teal-100 text-teal-700",
  ladder: "bg-green-100 text-green-700",
  helmet: "bg-red-100 text-red-700",
  safety_belt: "bg-indigo-100 text-indigo-700",
};

const UNITS = [
  { value: "pcs", label: "Pieces" },
  { value: "meters", label: "Meters" },
  { value: "kg", label: "Kilograms" },
  { value: "rolls", label: "Rolls" },
  { value: "sets", label: "Sets" },
  { value: "pairs", label: "Pairs" },
];

// =============================================================================
// GRN CREATION MODAL
// =============================================================================
function CreateGRNModal({ warehouses, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    supplier: "",
    supplier_invoice: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    to_warehouse_id: "",
    remarks: "",
  });
  const [items, setItems] = useState([
    { item_type: "meter", serial_numbers: "", make: "", model: "", quantity: 1, unit: "pcs", unit_price: 0 }
  ]);
  const [serialMode, setSerialMode] = useState("bulk"); // bulk or individual

  const addItem = () => {
    setItems([...items, { item_type: "meter", serial_numbers: "", make: "", model: "", quantity: 1, unit: "pcs", unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    
    // Auto-calculate quantity from serial numbers if bulk mode
    if (field === "serial_numbers" && serialMode === "bulk") {
      const serials = value.split(/[\n,;]+/).map(s => s.trim()).filter(s => s);
      updated[index].quantity = serials.length || 1;
    }
    
    setItems(updated);
  };

  const calculateTotals = () => {
    let totalItems = 0;
    let totalValue = 0;
    items.forEach(item => {
      totalItems += item.quantity || 0;
      totalValue += (item.quantity || 0) * (item.unit_price || 0);
    });
    return { totalItems, totalValue };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.supplier) {
      toast.error("Please enter supplier name");
      return;
    }
    if (!form.to_warehouse_id) {
      toast.error("Please select target warehouse");
      return;
    }
    if (items.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    // Validate items
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const itemType = ITEM_TYPES.find(t => t.value === item.item_type);
      
      if (itemType?.hasSerial && !item.serial_numbers?.trim()) {
        toast.error(`Line ${i + 1}: ${itemType.label} requires serial numbers`);
        return;
      }
      if (item.quantity < 1) {
        toast.error(`Line ${i + 1}: Quantity must be at least 1`);
        return;
      }
    }

    setLoading(true);
    try {
      // Prepare items array - expand serial numbers into individual items
      const processedItems = [];
      
      for (const item of items) {
        const itemType = ITEM_TYPES.find(t => t.value === item.item_type);
        
        if (itemType?.hasSerial && item.serial_numbers) {
          // Split serial numbers and create individual entries
          const serials = item.serial_numbers.split(/[\n,;]+/).map(s => s.trim()).filter(s => s);
          for (const serial of serials) {
            processedItems.push({
              item_type: item.item_type,
              serial_number: serial,
              make: item.make,
              model: item.model,
              quantity: 1,
              unit: item.unit,
              unit_price: item.unit_price,
            });
          }
        } else {
          // Non-serial items - add as bulk
          processedItems.push({
            item_type: item.item_type,
            serial_number: null,
            make: item.make,
            model: item.model,
            quantity: item.quantity,
            unit: item.unit,
            unit_price: item.unit_price,
          });
        }
      }

      const payload = {
        supplier: form.supplier,
        supplier_invoice: form.supplier_invoice,
        invoice_date: form.invoice_date,
        to_warehouse_id: form.to_warehouse_id,
        remarks: form.remarks,
        total_items: processedItems.reduce((sum, i) => sum + (i.quantity || 1), 0),
        total_value: processedItems.reduce((sum, i) => sum + ((i.quantity || 1) * (i.unit_price || 0)), 0),
        items: processedItems,
      };

      await api.post("/grn", payload);
      toast.success("GRN created successfully!");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create GRN");
    } finally {
      setLoading(false);
    }
  };

  const { totalItems, totalValue } = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-4xl my-8 animate-fade-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-500 to-orange-600">
          <div>
            <h2 className="text-lg font-bold text-white">Create Goods Receipt Note (GRN)</h2>
            <p className="text-orange-100 text-xs">Record incoming stock from supplier</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Supplier Info */}
          <div className="bg-slate-50 rounded-sm p-4 border border-slate-200">
            <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
              <FileText size={16} className="text-orange-500" />
              Supplier Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="field-label">Supplier Name *</label>
                <input
                  type="text"
                  className="field-input"
                  placeholder="e.g., Genus Power Infrastructures"
                  value={form.supplier}
                  onChange={e => setForm({ ...form, supplier: e.target.value })}
                  required
                  data-testid="grn-supplier"
                />
              </div>
              <div>
                <label className="field-label">Supplier Invoice #</label>
                <input
                  type="text"
                  className="field-input"
                  placeholder="e.g., INV-2026-0042"
                  value={form.supplier_invoice}
                  onChange={e => setForm({ ...form, supplier_invoice: e.target.value })}
                  data-testid="grn-invoice"
                />
              </div>
              <div>
                <label className="field-label">Invoice Date</label>
                <input
                  type="date"
                  className="field-input"
                  value={form.invoice_date}
                  onChange={e => setForm({ ...form, invoice_date: e.target.value })}
                  data-testid="grn-date"
                />
              </div>
            </div>
          </div>

          {/* Warehouse Selection */}
          <div className="bg-slate-50 rounded-sm p-4 border border-slate-200">
            <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
              <Warehouse size={16} className="text-orange-500" />
              Receiving Warehouse *
            </h3>
            <select
              className="field-select"
              value={form.to_warehouse_id}
              onChange={e => setForm({ ...form, to_warehouse_id: e.target.value })}
              required
              data-testid="grn-warehouse"
            >
              <option value="">Select Warehouse</option>
              {warehouses.map(wh => (
                <option key={wh.id} value={wh.id}>
                  {wh.code} - {wh.name} ({wh.circle_name})
                </option>
              ))}
            </select>
          </div>

          {/* Items Section */}
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
                <Package size={16} className="text-orange-500" />
                Line Items ({items.length})
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500">Serial Entry:</span>
                  <button
                    type="button"
                    onClick={() => setSerialMode("bulk")}
                    className={`px-2 py-1 rounded text-xs ${serialMode === "bulk" ? "bg-orange-500 text-white" : "bg-white text-slate-600"}`}
                  >
                    Bulk (Paste)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSerialMode("individual")}
                    className={`px-2 py-1 rounded text-xs ${serialMode === "individual" ? "bg-orange-500 text-white" : "bg-white text-slate-600"}`}
                  >
                    Individual
                  </button>
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-sm text-xs font-bold hover:bg-green-600"
                  data-testid="grn-add-item"
                >
                  <Plus size={14} />
                  Add Line
                </button>
              </div>
            </div>

            <div className="divide-y divide-slate-100">
              {items.map((item, index) => {
                const itemType = ITEM_TYPES.find(t => t.value === item.item_type);
                return (
                  <div key={index} className="p-4 bg-white hover:bg-slate-50 transition-colors">
                    <div className="flex items-start gap-4">
                      {/* Line Number */}
                      <div className="flex-shrink-0 w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-bold text-slate-600">
                        {index + 1}
                      </div>

                      {/* Item Details */}
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-6 gap-3">
                        {/* Item Type */}
                        <div className="md:col-span-2">
                          <label className="text-[10px] font-medium text-slate-500 uppercase">Item Type *</label>
                          <select
                            className="field-select text-sm"
                            value={item.item_type}
                            onChange={e => updateItem(index, "item_type", e.target.value)}
                            data-testid={`grn-item-type-${index}`}
                          >
                            {ITEM_TYPES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Make */}
                        <div>
                          <label className="text-[10px] font-medium text-slate-500 uppercase">Make/Brand</label>
                          <input
                            type="text"
                            className="field-input text-sm"
                            placeholder="e.g., Genus"
                            value={item.make}
                            onChange={e => updateItem(index, "make", e.target.value)}
                          />
                        </div>

                        {/* Model */}
                        <div>
                          <label className="text-[10px] font-medium text-slate-500 uppercase">Model</label>
                          <input
                            type="text"
                            className="field-input text-sm"
                            placeholder="e.g., G3P-100"
                            value={item.model}
                            onChange={e => updateItem(index, "model", e.target.value)}
                          />
                        </div>

                        {/* Quantity */}
                        <div>
                          <label className="text-[10px] font-medium text-slate-500 uppercase">Qty</label>
                          <input
                            type="number"
                            className="field-input text-sm"
                            min="1"
                            value={item.quantity}
                            onChange={e => updateItem(index, "quantity", parseInt(e.target.value) || 1)}
                            disabled={itemType?.hasSerial && serialMode === "bulk"}
                            data-testid={`grn-qty-${index}`}
                          />
                        </div>

                        {/* Unit Price */}
                        <div>
                          <label className="text-[10px] font-medium text-slate-500 uppercase">Unit Price (₹)</label>
                          <input
                            type="number"
                            className="field-input text-sm"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={e => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                          />
                        </div>

                        {/* Serial Numbers (if applicable) */}
                        {itemType?.hasSerial && (
                          <div className="md:col-span-6">
                            <label className="text-[10px] font-medium text-slate-500 uppercase flex items-center gap-1">
                              Serial Numbers *
                              <span className="text-orange-500">(one per line or comma-separated)</span>
                            </label>
                            <textarea
                              className="field-input text-sm font-mono"
                              rows={3}
                              placeholder="SM001234567&#10;SM001234568&#10;SM001234569"
                              value={item.serial_numbers}
                              onChange={e => updateItem(index, "serial_numbers", e.target.value)}
                              data-testid={`grn-serials-${index}`}
                            />
                            {item.serial_numbers && (
                              <div className="mt-1 text-xs text-blue-600">
                                {item.serial_numbers.split(/[\n,;]+/).filter(s => s.trim()).length} serial(s) detected
                              </div>
                            )}
                          </div>
                        )}

                        {/* Line Total */}
                        <div className="md:col-span-6 flex items-center justify-between bg-slate-50 rounded-sm p-2">
                          <span className="text-xs text-slate-500">
                            Line Total: <span className="font-bold text-slate-700">{item.quantity} {item.unit}</span>
                          </span>
                          <span className="text-sm font-bold text-green-600">
                            ₹{((item.quantity || 0) * (item.unit_price || 0)).toLocaleString("en-IN")}
                          </span>
                        </div>
                      </div>

                      {/* Remove Button */}
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="flex-shrink-0 p-2 text-red-500 hover:bg-red-50 rounded-sm"
                          data-testid={`grn-remove-${index}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className="field-label">Remarks / Notes</label>
            <textarea
              className="field-input"
              rows={2}
              placeholder="Any additional notes about this receipt..."
              value={form.remarks}
              onChange={e => setForm({ ...form, remarks: e.target.value })}
            />
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-sm p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-slate-400 text-xs uppercase">GRN Summary</div>
                <div className="flex items-center gap-6 mt-1">
                  <div>
                    <span className="text-slate-400 text-sm">Total Items: </span>
                    <span className="text-2xl font-black text-orange-400">{totalItems}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-sm">Total Value: </span>
                    <span className="text-2xl font-black text-green-400">₹{totalValue.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 border border-slate-600 text-slate-300 rounded-sm font-medium hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-orange-500 text-white rounded-sm font-bold hover:bg-orange-600 disabled:opacity-60 flex items-center gap-2"
                  data-testid="grn-submit"
                >
                  {loading ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Create GRN
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// ISSUANCE CREATION MODAL
// =============================================================================
function CreateIssuanceModal({ availableItems, employees, onClose, onCreated }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    issuance_type: "bulk",
    issued_to_type: "technician",
    issued_to_id: "",
    notes: "",
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showItemPicker, setShowItemPicker] = useState(false);

  // Filter available items (only in_stock items)
  const inStockItems = availableItems.filter(item => item.status === "in_stock");
  
  // Filter by search term
  const filteredItems = inStockItems.filter(item => {
    const term = searchTerm.toLowerCase();
    return (
      item.serial_number?.toLowerCase().includes(term) ||
      item.item_type?.toLowerCase().includes(term) ||
      item.make?.toLowerCase().includes(term) ||
      item.model?.toLowerCase().includes(term)
    );
  });

  // Filter employees based on issued_to_type
  const filteredEmployees = employees.filter(emp => {
    if (form.issued_to_type === "technician") {
      return emp.role === "technician";
    } else if (form.issued_to_type === "supervisor") {
      return emp.role === "supervisor";
    }
    return true;
  });

  const addItem = (item) => {
    if (!selectedItems.find(i => i.id === item.id)) {
      setSelectedItems([...selectedItems, { 
        ...item, 
        issue_quantity: item.quantity || 1 
      }]);
    }
    setShowItemPicker(false);
    setSearchTerm("");
  };

  const removeItem = (itemId) => {
    setSelectedItems(selectedItems.filter(i => i.id !== itemId));
  };

  const updateItemQuantity = (itemId, qty) => {
    setSelectedItems(selectedItems.map(i => 
      i.id === itemId ? { ...i, issue_quantity: qty } : i
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.issued_to_id) {
      toast.error("Please select a recipient");
      return;
    }
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item to issue");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        issuance_type: form.issuance_type,
        issued_to_type: form.issued_to_type,
        issued_to_id: form.issued_to_id,
        notes: form.notes,
        items: selectedItems.map(item => ({
          material_type: item.item_type,
          material_code: item.model,
          serial_number: item.serial_number,
          quantity: item.issue_quantity || 1,
          unit: item.unit || "pcs",
          inventory_item_id: item.id  // Link to source inventory item
        }))
      };

      await api.post("/inventory-issuance", payload);
      toast.success("Items issued successfully!");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create issuance");
    } finally {
      setLoading(false);
    }
  };

  const totalItems = selectedItems.reduce((sum, i) => sum + (i.issue_quantity || 1), 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl my-8 animate-fade-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-500 to-blue-600">
          <div>
            <h2 className="text-lg font-bold text-white">Issue Inventory</h2>
            <p className="text-blue-100 text-xs">Issue items from warehouse to field staff</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Issuance Type */}
          <div className="bg-slate-50 rounded-sm p-4 border border-slate-200">
            <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
              <Send size={16} className="text-blue-500" />
              Issuance Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="field-label">Issuance Type</label>
                <select
                  className="field-select"
                  value={form.issuance_type}
                  onChange={e => setForm({ ...form, issuance_type: e.target.value })}
                >
                  <option value="bulk">Bulk (Warehouse → Staff)</option>
                  <option value="micro">Micro (Supervisor → Technician)</option>
                </select>
              </div>
              <div>
                <label className="field-label">Issue To</label>
                <select
                  className="field-select"
                  value={form.issued_to_type}
                  onChange={e => setForm({ ...form, issued_to_type: e.target.value, issued_to_id: "" })}
                >
                  <option value="technician">Technician</option>
                  <option value="supervisor">Supervisor</option>
                </select>
              </div>
            </div>
          </div>

          {/* Recipient Selection */}
          <div className="bg-slate-50 rounded-sm p-4 border border-slate-200">
            <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
              <User size={16} className="text-blue-500" />
              Select Recipient *
            </h3>
            <select
              className="field-select"
              value={form.issued_to_id}
              onChange={e => setForm({ ...form, issued_to_id: e.target.value })}
              required
              data-testid="issuance-recipient"
            >
              <option value="">Select {form.issued_to_type}</option>
              {filteredEmployees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.full_name} ({emp.circle_name || "No Circle"})
                </option>
              ))}
            </select>
            {filteredEmployees.length === 0 && (
              <p className="text-xs text-orange-600 mt-2">No {form.issued_to_type}s found in the system</p>
            )}
          </div>

          {/* Item Selection */}
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
                <Package size={16} className="text-blue-500" />
                Items to Issue ({selectedItems.length})
              </h3>
              <button
                type="button"
                onClick={() => setShowItemPicker(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white rounded-sm text-xs font-bold hover:bg-blue-600"
                data-testid="add-issuance-item"
              >
                <Plus size={14} />
                Add Item
              </button>
            </div>

            {/* Selected Items List */}
            <div className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
              {selectedItems.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Package size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No items selected</p>
                  <p className="text-xs mt-1">Click "Add Item" to select items from stock</p>
                </div>
              ) : selectedItems.map((item, index) => (
                <div key={item.id} className="p-3 bg-white hover:bg-slate-50 flex items-center gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[item.item_type] || "bg-slate-100"}`}>
                        {item.item_type?.replace("_", " ")}
                      </span>
                      {item.serial_number && (
                        <span className="font-mono text-xs font-semibold text-slate-700">{item.serial_number}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{item.make} {item.model}</p>
                  </div>
                  {!item.serial_number && (
                    <div className="w-20">
                      <input
                        type="number"
                        className="field-input text-sm text-center"
                        min="1"
                        max={item.quantity}
                        value={item.issue_quantity}
                        onChange={e => updateItemQuantity(item.id, parseInt(e.target.value) || 1)}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="field-label">Notes</label>
            <textarea
              className="field-input"
              rows={2}
              placeholder="Any notes about this issuance..."
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-sm p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-slate-400 text-xs uppercase">Issuance Summary</div>
                <div className="flex items-center gap-4 mt-1">
                  <div>
                    <span className="text-slate-400 text-sm">Total Items: </span>
                    <span className="text-2xl font-black text-blue-400">{totalItems}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <Warehouse size={16} />
                    <ArrowRight size={14} />
                    <User size={16} />
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 border border-slate-600 text-slate-300 rounded-sm font-medium hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedItems.length === 0}
                  className="px-6 py-2.5 bg-blue-500 text-white rounded-sm font-bold hover:bg-blue-600 disabled:opacity-60 flex items-center gap-2"
                  data-testid="issuance-submit"
                >
                  {loading ? "Processing..." : (
                    <>
                      <Send size={16} />
                      Issue Items
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Item Picker Modal */}
        {showItemPicker && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl animate-fade-up">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="font-bold text-slate-700">Select Item from Stock</h3>
                <button onClick={() => setShowItemPicker(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
              
              {/* Search */}
              <div className="p-4 border-b border-slate-100">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    className="field-input pl-9"
                    placeholder="Search by serial, type, make..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              {/* Items List */}
              <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                {filteredItems.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <Package size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No items available in stock</p>
                  </div>
                ) : filteredItems.map(item => {
                  const isSelected = selectedItems.find(i => i.id === item.id);
                  return (
                    <div
                      key={item.id}
                      onClick={() => !isSelected && addItem(item)}
                      className={`p-3 flex items-center gap-3 cursor-pointer ${
                        isSelected ? "bg-blue-50 opacity-60" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[item.item_type] || "bg-slate-100"}`}>
                            {item.item_type?.replace("_", " ")}
                          </span>
                          {item.serial_number && (
                            <span className="font-mono text-sm font-semibold">{item.serial_number}</span>
                          )}
                          {isSelected && (
                            <span className="text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded">SELECTED</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {item.make} {item.model} {item.quantity > 1 ? `· ${item.quantity} ${item.unit}` : ""}
                        </p>
                      </div>
                      {!isSelected && (
                        <div className="text-blue-500">
                          <Plus size={18} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="p-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
                Showing {filteredItems.length} available items
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STOCK TRANSFER MODAL
// =============================================================================
function CreateTransferModal({ warehouses, items, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    from_warehouse_id: "",
    to_warehouse_id: "",
    reason: "",
    notes: "",
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showItemPicker, setShowItemPicker] = useState(false);

  // Get items available in source warehouse
  const sourceWarehouseItems = items.filter(item => 
    item.status === "in_stock" && 
    item.current_warehouse_id === form.from_warehouse_id
  );

  // Filter by search term
  const filteredItems = sourceWarehouseItems.filter(item => {
    const term = searchTerm.toLowerCase();
    return (
      item.serial_number?.toLowerCase().includes(term) ||
      item.item_type?.toLowerCase().includes(term) ||
      item.make?.toLowerCase().includes(term) ||
      item.model?.toLowerCase().includes(term)
    );
  });

  // Get available destination warehouses (excluding source)
  const destinationWarehouses = warehouses.filter(wh => wh.id !== form.from_warehouse_id);

  const addItem = (item) => {
    if (!selectedItems.find(i => i.id === item.id)) {
      setSelectedItems([...selectedItems, { ...item }]);
    }
    setShowItemPicker(false);
    setSearchTerm("");
  };

  const removeItem = (itemId) => {
    setSelectedItems(selectedItems.filter(i => i.id !== itemId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.from_warehouse_id) {
      toast.error("Please select source warehouse");
      return;
    }
    if (!form.to_warehouse_id) {
      toast.error("Please select destination warehouse");
      return;
    }
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item to transfer");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        from_warehouse_id: form.from_warehouse_id,
        to_warehouse_id: form.to_warehouse_id,
        reason: form.reason,
        notes: form.notes,
        items: selectedItems.map(item => ({
          inventory_item_id: item.id,
          serial_number: item.serial_number,
          item_type: item.item_type,
          make: item.make,
          model: item.model,
          quantity: 1
        }))
      };

      await api.post("/stock-transfers", payload);
      toast.success("Stock transfer completed!");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create transfer");
    } finally {
      setLoading(false);
    }
  };

  const fromWarehouse = warehouses.find(w => w.id === form.from_warehouse_id);
  const toWarehouse = warehouses.find(w => w.id === form.to_warehouse_id);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl my-8 animate-fade-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-purple-500 to-purple-600">
          <div>
            <h2 className="text-lg font-bold text-white">Stock Transfer</h2>
            <p className="text-purple-100 text-xs">Transfer items between warehouses</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Warehouse Selection */}
          <div className="bg-slate-50 rounded-sm p-4 border border-slate-200">
            <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
              <ArrowLeftRight size={16} className="text-purple-500" />
              Transfer Route
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              {/* From Warehouse */}
              <div className="md:col-span-2">
                <label className="field-label">From Warehouse *</label>
                <select
                  className="field-select"
                  value={form.from_warehouse_id}
                  onChange={e => {
                    setForm({ ...form, from_warehouse_id: e.target.value, to_warehouse_id: "" });
                    setSelectedItems([]); // Clear selected items when source changes
                  }}
                  required
                  data-testid="transfer-from-warehouse"
                >
                  <option value="">Select Source</option>
                  {warehouses.map(wh => (
                    <option key={wh.id} value={wh.id}>
                      {wh.code} - {wh.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <ArrowRight size={20} className="text-purple-600" />
                </div>
              </div>

              {/* To Warehouse */}
              <div className="md:col-span-2">
                <label className="field-label">To Warehouse *</label>
                <select
                  className="field-select"
                  value={form.to_warehouse_id}
                  onChange={e => setForm({ ...form, to_warehouse_id: e.target.value })}
                  required
                  disabled={!form.from_warehouse_id}
                  data-testid="transfer-to-warehouse"
                >
                  <option value="">Select Destination</option>
                  {destinationWarehouses.map(wh => (
                    <option key={wh.id} value={wh.id}>
                      {wh.code} - {wh.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Visual Route */}
            {fromWarehouse && toWarehouse && (
              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-sm">
                <div className="flex items-center justify-center gap-3 text-sm">
                  <div className="text-center">
                    <Warehouse size={20} className="mx-auto text-purple-600 mb-1" />
                    <p className="font-bold text-slate-700">{fromWarehouse.code}</p>
                    <p className="text-xs text-slate-500">{fromWarehouse.circle_name}</p>
                  </div>
                  <div className="flex-1 flex items-center justify-center">
                    <div className="flex-1 h-0.5 bg-purple-300"></div>
                    <ArrowRight size={20} className="text-purple-500 mx-2" />
                    <div className="flex-1 h-0.5 bg-purple-300"></div>
                  </div>
                  <div className="text-center">
                    <Warehouse size={20} className="mx-auto text-purple-600 mb-1" />
                    <p className="font-bold text-slate-700">{toWarehouse.code}</p>
                    <p className="text-xs text-slate-500">{toWarehouse.circle_name}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Item Selection */}
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
                <Package size={16} className="text-purple-500" />
                Items to Transfer ({selectedItems.length})
              </h3>
              <button
                type="button"
                onClick={() => setShowItemPicker(true)}
                disabled={!form.from_warehouse_id}
                className="flex items-center gap-1 px-3 py-1.5 bg-purple-500 text-white rounded-sm text-xs font-bold hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-transfer-item"
              >
                <Plus size={14} />
                Add Item
              </button>
            </div>

            {/* Selected Items List */}
            <div className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
              {!form.from_warehouse_id ? (
                <div className="p-8 text-center text-slate-400">
                  <Warehouse size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Select source warehouse first</p>
                </div>
              ) : selectedItems.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Package size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No items selected</p>
                  <p className="text-xs mt-1">Click "Add Item" to select items from {fromWarehouse?.code || "warehouse"}</p>
                  <p className="text-xs text-purple-500 mt-2">{sourceWarehouseItems.length} items available in source</p>
                </div>
              ) : selectedItems.map((item, index) => (
                <div key={item.id} className="p-3 bg-white hover:bg-slate-50 flex items-center gap-3">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-600">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[item.item_type] || "bg-slate-100"}`}>
                        {item.item_type?.replace("_", " ")}
                      </span>
                      {item.serial_number && (
                        <span className="font-mono text-xs font-semibold text-slate-700">{item.serial_number}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{item.make} {item.model}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Reason & Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Reason for Transfer</label>
              <select
                className="field-select"
                value={form.reason}
                onChange={e => setForm({ ...form, reason: e.target.value })}
              >
                <option value="">Select Reason</option>
                <option value="stock_balancing">Stock Balancing</option>
                <option value="demand_fulfillment">Demand Fulfillment</option>
                <option value="project_requirement">Project Requirement</option>
                <option value="warehouse_closure">Warehouse Closure</option>
                <option value="quality_issue">Quality Issue</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="field-label">Notes</label>
              <input
                type="text"
                className="field-input"
                placeholder="Additional notes..."
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-sm p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-slate-400 text-xs uppercase">Transfer Summary</div>
                <div className="flex items-center gap-4 mt-1">
                  <div>
                    <span className="text-slate-400 text-sm">Items: </span>
                    <span className="text-2xl font-black text-purple-400">{selectedItems.length}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <Warehouse size={16} />
                    <ArrowRight size={14} />
                    <Warehouse size={16} />
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 border border-slate-600 text-slate-300 rounded-sm font-medium hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedItems.length === 0}
                  className="px-6 py-2.5 bg-purple-500 text-white rounded-sm font-bold hover:bg-purple-600 disabled:opacity-60 flex items-center gap-2"
                  data-testid="transfer-submit"
                >
                  {loading ? "Processing..." : (
                    <>
                      <Repeat size={16} />
                      Transfer Items
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Item Picker Modal */}
        {showItemPicker && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl animate-fade-up">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="font-bold text-slate-700">Select Item from {fromWarehouse?.code}</h3>
                <button onClick={() => setShowItemPicker(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
              
              {/* Search */}
              <div className="p-4 border-b border-slate-100">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    className="field-input pl-9"
                    placeholder="Search by serial, type, make..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              {/* Items List */}
              <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                {filteredItems.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <Package size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No items available in {fromWarehouse?.code}</p>
                  </div>
                ) : filteredItems.map(item => {
                  const isSelected = selectedItems.find(i => i.id === item.id);
                  return (
                    <div
                      key={item.id}
                      onClick={() => !isSelected && addItem(item)}
                      className={`p-3 flex items-center gap-3 cursor-pointer ${
                        isSelected ? "bg-purple-50 opacity-60" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[item.item_type] || "bg-slate-100"}`}>
                            {item.item_type?.replace("_", " ")}
                          </span>
                          {item.serial_number && (
                            <span className="font-mono text-sm font-semibold">{item.serial_number}</span>
                          )}
                          {isSelected && (
                            <span className="text-[10px] bg-purple-500 text-white px-1.5 py-0.5 rounded">SELECTED</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{item.make} {item.model}</p>
                      </div>
                      {!isSelected && (
                        <div className="text-purple-500">
                          <Plus size={18} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="p-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
                Showing {filteredItems.length} items in {fromWarehouse?.code}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// CREATE RETURN MODAL
// =============================================================================
const RETURN_REASONS = [
  { value: "unused", label: "Unused" },
  { value: "defective", label: "Defective" },
  { value: "project_complete", label: "Project Complete" },
  { value: "wrong_item", label: "Wrong Item" },
  { value: "other", label: "Other" },
];

const ITEM_CONDITIONS = [
  { value: "good", label: "Good", color: "bg-green-100 text-green-700" },
  { value: "damaged", label: "Damaged", color: "bg-orange-100 text-orange-700" },
  { value: "defective", label: "Defective", color: "bg-red-100 text-red-700" },
];

function CreateReturnModal({ warehouses, employees, onClose, onCreated }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingItems, setFetchingItems] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState("");
  const [technicianItems, setTechnicianItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [notes, setNotes] = useState("");

  // Filter employees that could have issued items (technicians and supervisors)
  const fieldStaff = employees.filter(emp => 
    ["technician", "supervisor"].includes(emp.role)
  );

  // Fetch items issued to selected technician
  const fetchTechnicianItems = async (empId) => {
    if (!empId) {
      setTechnicianItems([]);
      return;
    }
    
    setFetchingItems(true);
    try {
      // As admin/manager, fetch items issued to this specific employee
      const res = await api.get(`/inventory`, { 
        params: { status: "issued", issued_to_id: empId, limit: 100 } 
      });
      setTechnicianItems(res.data.items || []);
    } catch (e) {
      console.error("Failed to fetch technician items:", e);
      setTechnicianItems([]);
    } finally {
      setFetchingItems(false);
    }
  };

  useEffect(() => {
    if (selectedTechnician) {
      fetchTechnicianItems(selectedTechnician);
      setSelectedItems([]);
    } else {
      setTechnicianItems([]);
      setSelectedItems([]);
    }
  }, [selectedTechnician]);

  const toggleItem = (item) => {
    const isSelected = selectedItems.find(i => i.id === item.id);
    if (isSelected) {
      setSelectedItems(selectedItems.filter(i => i.id !== item.id));
    } else {
      setSelectedItems([...selectedItems, { 
        ...item, 
        return_reason: "unused", 
        condition: "good" 
      }]);
    }
  };

  const updateItemReason = (itemId, reason) => {
    setSelectedItems(selectedItems.map(i => 
      i.id === itemId ? { ...i, return_reason: reason } : i
    ));
  };

  const updateItemCondition = (itemId, condition) => {
    setSelectedItems(selectedItems.map(i => 
      i.id === itemId ? { ...i, condition: condition } : i
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!toWarehouseId) {
      toast.error("Please select a destination warehouse");
      return;
    }
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item to return");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        items: selectedItems.map(item => ({
          inventory_item_id: item.id,
          serial_number: item.serial_number,
          item_type: item.item_type,
          condition: item.condition,
          return_reason: item.return_reason,
        })),
        to_warehouse_id: toWarehouseId,
        on_behalf_of_employee_id: selectedTechnician,  // Process return on behalf of the selected technician
        notes: notes,
      };

      await api.post("/inventory-issuance/returns", payload);
      toast.success("Return processed successfully!");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to process return");
    } finally {
      setLoading(false);
    }
  };

  const selectedTechnicianName = fieldStaff.find(e => e.id === selectedTechnician)?.full_name;
  const toWarehouse = warehouses.find(w => w.id === toWarehouseId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl my-8 animate-fade-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-red-500 to-red-600">
          <div>
            <h2 className="text-lg font-bold text-white">Process Return</h2>
            <p className="text-red-100 text-xs">Return items from field staff to warehouse</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Technician Selection */}
          <div className="bg-slate-50 rounded-sm p-4 border border-slate-200">
            <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
              <User size={16} className="text-red-500" />
              Select Field Staff
            </h3>
            <select
              className="field-select"
              value={selectedTechnician}
              onChange={e => setSelectedTechnician(e.target.value)}
              data-testid="return-technician-select"
            >
              <option value="">Select Technician/Supervisor</option>
              {fieldStaff.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.full_name} ({emp.role}) - {emp.circle_name || "No Circle"}
                </option>
              ))}
            </select>
            {selectedTechnician && (
              <p className="text-xs text-slate-500 mt-2">
                {fetchingItems ? "Loading items..." : `${technicianItems.length} item(s) currently issued to ${selectedTechnicianName}`}
              </p>
            )}
          </div>

          {/* Items to Return */}
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
                <Package size={16} className="text-red-500" />
                Items to Return ({selectedItems.length})
              </h3>
            </div>

            <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
              {!selectedTechnician ? (
                <div className="p-8 text-center text-slate-400">
                  <User size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Select a technician/supervisor first</p>
                </div>
              ) : fetchingItems ? (
                <div className="p-8 text-center text-slate-400">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400 mx-auto mb-2"></div>
                  <p>Loading issued items...</p>
                </div>
              ) : technicianItems.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Package size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No items currently issued to this person</p>
                </div>
              ) : technicianItems.map(item => {
                const isSelected = selectedItems.find(i => i.id === item.id);
                const selectedItem = selectedItems.find(i => i.id === item.id);
                return (
                  <div key={item.id} className={`p-3 hover:bg-slate-50 ${isSelected ? "bg-red-50" : ""}`}>
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <div 
                        onClick={() => toggleItem(item)}
                        className={`flex-shrink-0 w-5 h-5 rounded border-2 cursor-pointer flex items-center justify-center mt-0.5 ${
                          isSelected ? "bg-red-500 border-red-500 text-white" : "border-slate-300"
                        }`}
                        data-testid={`return-item-${item.id}`}
                      >
                        {isSelected && <CheckCircle size={12} />}
                      </div>
                      
                      {/* Item Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[item.item_type] || "bg-slate-100"}`}>
                            {item.item_type?.replace("_", " ")}
                          </span>
                          {item.serial_number && (
                            <span className="font-mono text-xs font-semibold text-slate-700">{item.serial_number}</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{item.make} {item.model}</p>
                        
                        {/* Reason & Condition (only shown when selected) */}
                        {isSelected && (
                          <div className="mt-2 flex items-center gap-3">
                            <div>
                              <label className="text-[10px] font-medium text-slate-500 uppercase">Reason</label>
                              <select
                                className="field-select text-xs py-1"
                                value={selectedItem?.return_reason || "unused"}
                                onChange={e => updateItemReason(item.id, e.target.value)}
                                onClick={e => e.stopPropagation()}
                                data-testid={`return-reason-${item.id}`}
                              >
                                {RETURN_REASONS.map(r => (
                                  <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-[10px] font-medium text-slate-500 uppercase">Condition</label>
                              <select
                                className="field-select text-xs py-1"
                                value={selectedItem?.condition || "good"}
                                onChange={e => updateItemCondition(item.id, e.target.value)}
                                onClick={e => e.stopPropagation()}
                                data-testid={`return-condition-${item.id}`}
                              >
                                {ITEM_CONDITIONS.map(c => (
                                  <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Destination Warehouse */}
          <div className="bg-slate-50 rounded-sm p-4 border border-slate-200">
            <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
              <Warehouse size={16} className="text-red-500" />
              Return To Warehouse *
            </h3>
            <select
              className="field-select"
              value={toWarehouseId}
              onChange={e => setToWarehouseId(e.target.value)}
              required
              data-testid="return-warehouse-select"
            >
              <option value="">Select Warehouse</option>
              {warehouses.map(wh => (
                <option key={wh.id} value={wh.id}>
                  {wh.code} - {wh.name} ({wh.circle_name})
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="field-label">Notes</label>
            <textarea
              className="field-input"
              rows={2}
              placeholder="Any notes about this return..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              data-testid="return-notes"
            />
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-sm p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-slate-400 text-xs uppercase">Return Summary</div>
                <div className="flex items-center gap-4 mt-1">
                  <div>
                    <span className="text-slate-400 text-sm">Items: </span>
                    <span className="text-2xl font-black text-red-400">{selectedItems.length}</span>
                  </div>
                  {toWarehouse && (
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <User size={16} />
                      <ArrowRight size={14} />
                      <Warehouse size={16} />
                      <span className="text-xs">{toWarehouse.code}</span>
                    </div>
                  )}
                </div>
                {selectedItems.length > 0 && (
                  <div className="mt-2 text-xs text-slate-400">
                    {selectedItems.filter(i => i.condition === "good").length} Good · 
                    {selectedItems.filter(i => i.condition === "damaged").length} Damaged · 
                    {selectedItems.filter(i => i.condition === "defective").length} Defective
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 border border-slate-600 text-slate-300 rounded-sm font-medium hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedItems.length === 0}
                  className="px-6 py-2.5 bg-red-500 text-white rounded-sm font-bold hover:bg-red-600 disabled:opacity-60 flex items-center gap-2"
                  data-testid="return-submit"
                >
                  {loading ? "Processing..." : (
                    <>
                      <Undo2 size={16} />
                      Process Return
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// CREATE STOCK ADJUSTMENT MODAL
// =============================================================================
const ADJUSTMENT_TYPES = [
  { value: "physical_count", label: "Physical Count Reconciliation" },
  { value: "damage", label: "Damage Recording" },
  { value: "loss", label: "Loss Recording" },
  { value: "status_correction", label: "Status Correction" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "in_stock", label: "In Stock", color: "bg-green-100 text-green-700" },
  { value: "damaged", label: "Damaged", color: "bg-orange-100 text-orange-700" },
  { value: "defective", label: "Defective", color: "bg-red-100 text-red-700" },
  { value: "lost", label: "Lost", color: "bg-gray-100 text-gray-700" },
  { value: "scrapped", label: "Scrapped", color: "bg-slate-100 text-slate-700" },
];

function CreateAdjustmentModal({ warehouses, items, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    warehouse_id: "",
    adjustment_type: "physical_count",
    notes: "",
    reference_doc: "",
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showItemPicker, setShowItemPicker] = useState(false);

  // Get items in selected warehouse
  const warehouseItems = items.filter(item => 
    item.current_warehouse_id === form.warehouse_id && 
    item.status !== "issued"  // Can't adjust issued items
  );

  // Filter by search term
  const filteredItems = warehouseItems.filter(item => {
    const term = searchTerm.toLowerCase();
    return (
      item.serial_number?.toLowerCase().includes(term) ||
      item.item_type?.toLowerCase().includes(term) ||
      item.make?.toLowerCase().includes(term) ||
      item.model?.toLowerCase().includes(term)
    );
  });

  const addItem = (item) => {
    if (!selectedItems.find(i => i.id === item.id)) {
      setSelectedItems([...selectedItems, { 
        ...item, 
        new_status: item.status,
        reason: ""
      }]);
    }
    setShowItemPicker(false);
    setSearchTerm("");
  };

  const removeItem = (itemId) => {
    setSelectedItems(selectedItems.filter(i => i.id !== itemId));
  };

  const updateItemStatus = (itemId, newStatus) => {
    setSelectedItems(selectedItems.map(i => 
      i.id === itemId ? { ...i, new_status: newStatus } : i
    ));
  };

  const updateItemReason = (itemId, reason) => {
    setSelectedItems(selectedItems.map(i => 
      i.id === itemId ? { ...i, reason: reason } : i
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.warehouse_id) {
      toast.error("Please select a warehouse");
      return;
    }
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item to adjust");
      return;
    }
    
    // Validate all items have reasons
    const itemsWithoutReason = selectedItems.filter(i => i.new_status !== i.status && !i.reason);
    if (itemsWithoutReason.length > 0) {
      toast.error("Please provide a reason for all status changes");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        warehouse_id: form.warehouse_id,
        adjustment_type: form.adjustment_type,
        items: selectedItems.map(item => ({
          inventory_item_id: item.id,
          serial_number: item.serial_number,
          item_type: item.item_type,
          current_status: item.status,
          new_status: item.new_status,
          reason: item.reason || "Stock adjustment"
        })),
        notes: form.notes,
        reference_doc: form.reference_doc,
      };

      await api.post("/stock-adjustments", payload);
      toast.success("Stock adjustment created successfully!");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create adjustment");
    } finally {
      setLoading(false);
    }
  };

  const selectedWarehouse = warehouses.find(w => w.id === form.warehouse_id);
  const changedItems = selectedItems.filter(i => i.new_status !== i.status);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl my-8 animate-fade-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-amber-500 to-amber-600">
          <div>
            <h2 className="text-lg font-bold text-white">Stock Adjustment</h2>
            <p className="text-amber-100 text-xs">Reconcile physical stock, record damage/loss</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Warehouse & Type Selection */}
          <div className="bg-slate-50 rounded-sm p-4 border border-slate-200">
            <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
              <AlertOctagon size={16} className="text-amber-500" />
              Adjustment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="field-label">Warehouse *</label>
                <select
                  className="field-select"
                  value={form.warehouse_id}
                  onChange={e => {
                    setForm({ ...form, warehouse_id: e.target.value });
                    setSelectedItems([]);
                  }}
                  required
                  data-testid="adjustment-warehouse"
                >
                  <option value="">Select Warehouse</option>
                  {warehouses.map(wh => (
                    <option key={wh.id} value={wh.id}>
                      {wh.code} - {wh.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label">Adjustment Type *</label>
                <select
                  className="field-select"
                  value={form.adjustment_type}
                  onChange={e => setForm({ ...form, adjustment_type: e.target.value })}
                  data-testid="adjustment-type"
                >
                  {ADJUSTMENT_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Item Selection */}
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
                <Package size={16} className="text-amber-500" />
                Items to Adjust ({selectedItems.length})
              </h3>
              <button
                type="button"
                onClick={() => setShowItemPicker(true)}
                disabled={!form.warehouse_id}
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-sm text-xs font-bold hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-adjustment-item"
              >
                <Plus size={14} />
                Add Item
              </button>
            </div>

            {/* Selected Items List */}
            <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
              {!form.warehouse_id ? (
                <div className="p-8 text-center text-slate-400">
                  <Warehouse size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Select a warehouse first</p>
                </div>
              ) : selectedItems.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Package size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No items selected</p>
                  <p className="text-xs mt-1">{warehouseItems.length} items available in {selectedWarehouse?.code}</p>
                </div>
              ) : selectedItems.map((item, index) => (
                <div key={item.id} className="p-3 bg-white hover:bg-slate-50">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs font-bold text-amber-600">
                      {index + 1}
                    </div>
                    <div className="flex-1 space-y-2">
                      {/* Item Info */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[item.item_type] || "bg-slate-100"}`}>
                          {item.item_type?.replace("_", " ")}
                        </span>
                        {item.serial_number && (
                          <span className="font-mono text-xs font-semibold text-slate-700">{item.serial_number}</span>
                        )}
                        <span className="text-xs text-slate-500">{item.make} {item.model}</span>
                      </div>
                      
                      {/* Status Change */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${STATUS_OPTIONS.find(s => s.value === item.status)?.color || "bg-slate-100"}`}>
                            {item.status?.replace("_", " ")}
                          </span>
                          <ArrowRight size={14} className="text-slate-400" />
                          <select
                            className="field-select text-xs py-1 w-32"
                            value={item.new_status}
                            onChange={e => updateItemStatus(item.id, e.target.value)}
                            data-testid={`adjustment-status-${item.id}`}
                          >
                            {STATUS_OPTIONS.map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        {item.new_status !== item.status && (
                          <input
                            type="text"
                            className="field-input text-xs py-1 flex-1"
                            placeholder="Reason for change *"
                            value={item.reason}
                            onChange={e => updateItemReason(item.id, e.target.value)}
                            data-testid={`adjustment-reason-${item.id}`}
                          />
                        )}
                      </div>
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reference & Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Reference Document</label>
              <input
                type="text"
                className="field-input"
                placeholder="e.g., Audit Report #123"
                value={form.reference_doc}
                onChange={e => setForm({ ...form, reference_doc: e.target.value })}
                data-testid="adjustment-reference"
              />
            </div>
            <div>
              <label className="field-label">Notes</label>
              <input
                type="text"
                className="field-input"
                placeholder="Additional notes..."
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                data-testid="adjustment-notes"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-sm p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-slate-400 text-xs uppercase">Adjustment Summary</div>
                <div className="flex items-center gap-4 mt-1">
                  <div>
                    <span className="text-slate-400 text-sm">Total Items: </span>
                    <span className="text-2xl font-black text-amber-400">{selectedItems.length}</span>
                  </div>
                  {changedItems.length > 0 && (
                    <div>
                      <span className="text-slate-400 text-sm">Status Changes: </span>
                      <span className="text-xl font-bold text-orange-400">{changedItems.length}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 border border-slate-600 text-slate-300 rounded-sm font-medium hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedItems.length === 0}
                  className="px-6 py-2.5 bg-amber-500 text-white rounded-sm font-bold hover:bg-amber-600 disabled:opacity-60 flex items-center gap-2"
                  data-testid="adjustment-submit"
                >
                  {loading ? "Processing..." : (
                    <>
                      <CheckCircle size={16} />
                      Create Adjustment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Item Picker Modal */}
        {showItemPicker && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl animate-fade-up">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="font-bold text-slate-700">Select Item from {selectedWarehouse?.code}</h3>
                <button onClick={() => setShowItemPicker(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
              
              {/* Search */}
              <div className="p-4 border-b border-slate-100">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    className="field-input pl-9"
                    placeholder="Search by serial, type, make..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              {/* Items List */}
              <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                {filteredItems.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <Package size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No items available in {selectedWarehouse?.code}</p>
                  </div>
                ) : filteredItems.map(item => {
                  const isSelected = selectedItems.find(i => i.id === item.id);
                  return (
                    <div
                      key={item.id}
                      onClick={() => !isSelected && addItem(item)}
                      className={`p-3 flex items-center gap-3 cursor-pointer ${
                        isSelected ? "bg-amber-50 opacity-60" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[item.item_type] || "bg-slate-100"}`}>
                            {item.item_type?.replace("_", " ")}
                          </span>
                          {item.serial_number && (
                            <span className="font-mono text-sm font-semibold">{item.serial_number}</span>
                          )}
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${STATUS_OPTIONS.find(s => s.value === item.status)?.color || "bg-slate-100"}`}>
                            {item.status?.replace("_", " ")}
                          </span>
                          {isSelected && (
                            <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded">SELECTED</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{item.make} {item.model}</p>
                      </div>
                      {!isSelected && (
                        <div className="text-amber-500">
                          <Plus size={18} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="p-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
                Showing {filteredItems.length} items in {selectedWarehouse?.code}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// REPORTS TAB COMPONENT
// =============================================================================
const REPORT_TYPES = [
  { value: "stock-ledger", label: "Stock Ledger", description: "Opening + In - Out = Closing" },
  { value: "movement", label: "Movement Report", description: "All stock movements & events" },
  { value: "inventory-summary", label: "Inventory Summary", description: "Current stock by warehouse" },
  { value: "consumption", label: "Consumption Report", description: "Items used in installations" },
];

const EVENT_TYPE_COLORS = {
  grn_created: "bg-green-100 text-green-700",
  issued: "bg-blue-100 text-blue-700",
  stock_transfer: "bg-purple-100 text-purple-700",
  return: "bg-red-100 text-red-700",
  stock_adjustment: "bg-amber-100 text-amber-700",
};

function ReportsTab({ warehouses }) {
  const [reportType, setReportType] = useState("stock-ledger");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    warehouse_id: "",
    item_type: "",
    from_date: "",
    to_date: "",
    event_type: "",
  });

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id;
      if (filters.item_type) params.item_type = filters.item_type;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      if (filters.event_type && reportType === "movement") params.event_type = filters.event_type;

      const res = await api.get(`/reports/${reportType}`, { params });
      setReportData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [reportType]);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const exportCSV = () => {
    if (!reportData) return;
    
    let csvContent = "";
    let fileName = `${reportType}_${new Date().toISOString().split('T')[0]}.csv`;

    if (reportType === "stock-ledger" && reportData.data) {
      csvContent = "Item Type,Warehouse,Circle,Opening,GRN,Returns,Transfers In,Total In,Issuances,Transfers Out,Adjustments,Total Out,Closing\n";
      reportData.data.forEach(row => {
        csvContent += `${row.item_type},${row.warehouse_code},${row.circle_name},${row.opening_balance},${row.receipts.grn},${row.receipts.returns},${row.receipts.transfers_in},${row.receipts.total},${row.issues.issuances},${row.issues.transfers_out},${row.issues.adjustments},${row.issues.total},${row.closing_balance}\n`;
      });
    } else if (reportType === "movement" && reportData.items) {
      csvContent = "Date,Event Type,Reference,Details,Warehouse\n";
      reportData.items.forEach(row => {
        const date = new Date(row.created_at).toLocaleDateString("en-IN");
        csvContent += `${date},${row.event_type},${row.reference_number || "-"},${row.details?.replace(/,/g, ";")},${row.warehouse_id || "-"}\n`;
      });
    } else if (reportType === "inventory-summary" && reportData.warehouses) {
      csvContent = "Warehouse,Circle,Item Type,In Stock,Issued,Installed,Damaged,Defective,Lost,Total\n";
      reportData.warehouses.forEach(wh => {
        Object.entries(wh.items || {}).forEach(([itemType, counts]) => {
          csvContent += `${wh.warehouse_code},${wh.circle_name},${itemType},${counts.in_stock},${counts.issued},${counts.installed},${counts.damaged},${counts.defective},${counts.lost},${counts.total}\n`;
        });
      });
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported!");
  };

  return (
    <div className="space-y-4" data-testid="reports-tab">
      {/* Report Type Selector */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex-1 min-w-[200px]">
            <label className="field-label">Report Type</label>
            <select
              className="field-select"
              value={reportType}
              onChange={e => setReportType(e.target.value)}
              data-testid="report-type-select"
            >
              {REPORT_TYPES.map(r => (
                <option key={r.value} value={r.value}>{r.label} - {r.description}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchReport}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold text-sm hover:bg-orange-600 disabled:opacity-50"
              data-testid="refresh-report"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={exportCSV}
              disabled={!reportData || loading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-sm font-bold text-sm hover:bg-slate-800 disabled:opacity-50"
              data-testid="export-csv"
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-1 text-slate-500">
            <Filter size={14} />
            <span className="text-xs font-medium">Filters:</span>
          </div>
          <div className="w-40">
            <label className="text-[10px] font-medium text-slate-500 uppercase">Warehouse</label>
            <select
              className="field-select text-xs py-1.5"
              value={filters.warehouse_id}
              onChange={e => handleFilterChange("warehouse_id", e.target.value)}
            >
              <option value="">All Warehouses</option>
              {warehouses.map(wh => (
                <option key={wh.id} value={wh.id}>{wh.code}</option>
              ))}
            </select>
          </div>
          <div className="w-32">
            <label className="text-[10px] font-medium text-slate-500 uppercase">Item Type</label>
            <select
              className="field-select text-xs py-1.5"
              value={filters.item_type}
              onChange={e => handleFilterChange("item_type", e.target.value)}
            >
              <option value="">All Types</option>
              {ITEM_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          {reportType === "movement" && (
            <div className="w-36">
              <label className="text-[10px] font-medium text-slate-500 uppercase">Event Type</label>
              <select
                className="field-select text-xs py-1.5"
                value={filters.event_type}
                onChange={e => handleFilterChange("event_type", e.target.value)}
              >
                <option value="">All Events</option>
                <option value="grn_created">GRN Created</option>
                <option value="issued">Issued</option>
                <option value="stock_transfer">Transfer</option>
                <option value="return">Return</option>
                <option value="stock_adjustment">Adjustment</option>
              </select>
            </div>
          )}
          <div className="w-36">
            <label className="text-[10px] font-medium text-slate-500 uppercase">From Date</label>
            <input
              type="date"
              className="field-input text-xs py-1.5"
              value={filters.from_date}
              onChange={e => handleFilterChange("from_date", e.target.value)}
            />
          </div>
          <div className="w-36">
            <label className="text-[10px] font-medium text-slate-500 uppercase">To Date</label>
            <input
              type="date"
              className="field-input text-xs py-1.5"
              value={filters.to_date}
              onChange={e => handleFilterChange("to_date", e.target.value)}
            />
          </div>
          <button
            onClick={fetchReport}
            className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded text-xs font-semibold hover:bg-slate-200"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-slate-500">Loading report...</p>
        </div>
      ) : !reportData ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">
          <ClipboardList size={48} className="mx-auto mb-4 opacity-50" />
          <p>Select a report type and click Refresh</p>
        </div>
      ) : (
        <>
          {/* Stock Ledger Report */}
          {reportType === "stock-ledger" && (
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-bold text-slate-700 flex items-center gap-2">
                  <BarChart2 size={16} className="text-orange-500" />
                  Stock Ledger Report
                </h3>
                <span className="text-xs text-slate-500">
                  Generated: {new Date(reportData.generated_at).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="dt-table">
                  <thead>
                    <tr>
                      <th rowSpan={2}>Item Type</th>
                      <th rowSpan={2}>Warehouse</th>
                      <th rowSpan={2}>Opening</th>
                      <th colSpan={4} className="text-center bg-green-50 text-green-700">Receipts (In)</th>
                      <th colSpan={4} className="text-center bg-red-50 text-red-700">Issues (Out)</th>
                      <th rowSpan={2}>Closing</th>
                    </tr>
                    <tr>
                      <th className="bg-green-50 text-green-700 text-[10px]">GRN</th>
                      <th className="bg-green-50 text-green-700 text-[10px]">Returns</th>
                      <th className="bg-green-50 text-green-700 text-[10px]">Transfer In</th>
                      <th className="bg-green-50 text-green-700 text-[10px] font-black">Total</th>
                      <th className="bg-red-50 text-red-700 text-[10px]">Issued</th>
                      <th className="bg-red-50 text-red-700 text-[10px]">Transfer Out</th>
                      <th className="bg-red-50 text-red-700 text-[10px]">Adjusted</th>
                      <th className="bg-red-50 text-red-700 text-[10px] font-black">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.data?.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="text-center py-8 text-slate-400">No data for selected filters</td>
                      </tr>
                    ) : reportData.data?.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[row.item_type] || "bg-slate-100"}`}>
                            {row.item_type?.replace("_", " ")}
                          </span>
                        </td>
                        <td>
                          <span className="font-mono text-xs">{row.warehouse_code}</span>
                          <div className="text-[10px] text-slate-400">{row.circle_name}</div>
                        </td>
                        <td className="font-semibold text-slate-700">{row.opening_balance}</td>
                        <td className="text-green-600">{row.receipts?.grn || 0}</td>
                        <td className="text-green-600">{row.receipts?.returns || 0}</td>
                        <td className="text-green-600">{row.receipts?.transfers_in || 0}</td>
                        <td className="font-bold text-green-700 bg-green-50">{row.receipts?.total || 0}</td>
                        <td className="text-red-600">{row.issues?.issuances || 0}</td>
                        <td className="text-red-600">{row.issues?.transfers_out || 0}</td>
                        <td className="text-red-600">{row.issues?.adjustments || 0}</td>
                        <td className="font-bold text-red-700 bg-red-50">{row.issues?.total || 0}</td>
                        <td className="font-black text-lg text-slate-900">{row.closing_balance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Movement Report */}
          {reportType === "movement" && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(reportData.summary || {}).map(([eventType, count]) => (
                  <div key={eventType} className="bg-white border border-slate-200 rounded-sm p-3">
                    <div className={`px-2 py-0.5 rounded text-[10px] font-bold w-fit ${EVENT_TYPE_COLORS[eventType] || "bg-slate-100"}`}>
                      {eventType.replace("_", " ")}
                    </div>
                    <div className="text-2xl font-black text-slate-900 mt-1">{count}</div>
                  </div>
                ))}
              </div>
              
              {/* Events Table */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-bold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={16} className="text-orange-500" />
                    Movement History ({reportData.total} events)
                  </h3>
                </div>
                <div className="overflow-x-auto max-h-96">
                  <table className="dt-table">
                    <thead>
                      <tr>
                        <th>Date/Time</th>
                        <th>Event Type</th>
                        <th>Reference</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.items?.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-8 text-slate-400">No movement events found</td>
                        </tr>
                      ) : reportData.items?.map(event => (
                        <tr key={event.id}>
                          <td className="text-xs text-slate-600 whitespace-nowrap">
                            {new Date(event.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                          </td>
                          <td>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${EVENT_TYPE_COLORS[event.event_type] || "bg-slate-100"}`}>
                              {event.event_type?.replace("_", " ")}
                            </span>
                          </td>
                          <td>
                            <span className="font-mono text-xs font-semibold text-slate-700">
                              {event.reference_number || "-"}
                            </span>
                          </td>
                          <td className="text-xs text-slate-600 max-w-md truncate">{event.details}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Inventory Summary Report */}
          {reportType === "inventory-summary" && (
            <div className="space-y-4">
              {/* Totals Card */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-sm p-4 text-white">
                <div className="text-slate-400 text-xs uppercase mb-2">Overall Totals</div>
                <div className="grid grid-cols-3 md:grid-cols-7 gap-4">
                  <div>
                    <div className="text-slate-400 text-[10px]">In Stock</div>
                    <div className="text-2xl font-black text-green-400">{reportData.totals?.in_stock || 0}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-[10px]">Issued</div>
                    <div className="text-2xl font-black text-blue-400">{reportData.totals?.issued || 0}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-[10px]">Installed</div>
                    <div className="text-2xl font-black text-purple-400">{reportData.totals?.installed || 0}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-[10px]">Damaged</div>
                    <div className="text-2xl font-black text-orange-400">{reportData.totals?.damaged || 0}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-[10px]">Defective</div>
                    <div className="text-2xl font-black text-red-400">{reportData.totals?.defective || 0}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-[10px]">Lost</div>
                    <div className="text-2xl font-black text-gray-400">{reportData.totals?.lost || 0}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-[10px]">Total</div>
                    <div className="text-2xl font-black text-white">{reportData.totals?.total || 0}</div>
                  </div>
                </div>
              </div>

              {/* Warehouse Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reportData.warehouses?.map(wh => (
                  <div key={wh.warehouse_id} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className="font-mono text-sm font-bold text-slate-800">{wh.warehouse_code}</div>
                      <div className="text-[10px] text-slate-500">{wh.warehouse_name} · {wh.circle_name}</div>
                    </div>
                    <div className="p-3 space-y-2">
                      {Object.entries(wh.items || {}).map(([itemType, counts]) => (
                        <div key={itemType} className="flex items-center justify-between py-1 border-b border-slate-50 last:border-0">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[itemType] || "bg-slate-100"}`}>
                            {itemType?.replace("_", " ")}
                          </span>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-green-600 font-semibold" title="In Stock">{counts.in_stock}</span>
                            {counts.issued > 0 && <span className="text-blue-600" title="Issued">{counts.issued}i</span>}
                            {counts.damaged > 0 && <span className="text-orange-600" title="Damaged">{counts.damaged}d</span>}
                            <span className="text-slate-400">/ {counts.total}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Consumption Report */}
          {reportType === "consumption" && (
            <div className="space-y-4">
              {/* Total Consumed */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-sm p-4 text-white">
                <div className="text-purple-200 text-xs uppercase">Total Items Consumed (Installed)</div>
                <div className="text-4xl font-black">{reportData.total_consumed || 0}</div>
              </div>

              {reportData.total_consumed === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">
                  <Package size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No consumption data found</p>
                  <p className="text-xs mt-1">Items with status "installed" will appear here</p>
                </div>
              ) : (
                <>
                  {/* Top Technicians */}
                  {reportData.top_technicians?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                        <h3 className="font-bold text-slate-700 flex items-center gap-2">
                          <Users size={16} className="text-purple-500" />
                          Top Technicians by Installation
                        </h3>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          {reportData.top_technicians.map((tech, idx) => (
                            <div key={tech.id || idx} className="bg-slate-50 rounded p-3 text-center">
                              <div className="text-2xl font-black text-purple-600">{tech.count}</div>
                              <div className="text-xs text-slate-600 truncate">{tech.name || "Unknown"}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* By Item Type */}
                  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <h3 className="font-bold text-slate-700">Consumption by Item Type & Circle</h3>
                    </div>
                    <table className="dt-table">
                      <thead>
                        <tr>
                          <th>Item Type</th>
                          <th>Circle</th>
                          <th>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.by_item_type?.map((row, idx) => (
                          <tr key={idx}>
                            <td>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[row.item_type] || "bg-slate-100"}`}>
                                {row.item_type?.replace("_", " ")}
                              </span>
                            </td>
                            <td className="text-sm text-slate-600">{row.circle_name || "-"}</td>
                            <td className="font-bold text-purple-600">{row.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// MAIN INVENTORY PAGE
// =============================================================================
export default function InventoryPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("summary");
  const [warehouses, setWarehouses] = useState([]);
  const [summary, setSummary] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grns, setGRNs] = useState([]);
  const [issuances, setIssuances] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [returns, setReturns] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showGRNModal, setShowGRNModal] = useState(false);
  const [showIssuanceModal, setShowIssuanceModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);

  const canCreateGRN = ["admin", "warehouse_manager"].includes(user?.role);
  const canCreateIssuance = ["admin", "warehouse_manager", "supervisor", "ci"].includes(user?.role);
  const canCreateTransfer = ["admin", "warehouse_manager"].includes(user?.role);
  const canCreateReturn = ["admin", "warehouse_manager", "supervisor", "ci"].includes(user?.role);
  const canCreateAdjustment = ["admin", "warehouse_manager"].includes(user?.role);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [whRes, sumRes, itemsRes, grnRes, issuanceRes, transferRes, returnsRes, adjustmentsRes, empRes] = await Promise.all([
        api.get("/warehouses"),
        api.get("/inventory/summary"),
        api.get("/inventory", { params: { limit: 100 } }),
        api.get("/grn", { params: { limit: 50 } }),
        api.get("/inventory-issuance", { params: { limit: 50 } }),
        api.get("/stock-transfers", { params: { limit: 50 } }),
        api.get("/inventory-issuance/returns", { params: { limit: 50 } }),
        api.get("/stock-adjustments", { params: { limit: 50 } }),
        api.get("/employees", { params: { limit: 200 } })
      ]);
      setWarehouses(whRes.data);
      setSummary(sumRes.data);
      setItems(itemsRes.data.items || []);
      setGRNs(grnRes.data.items || []);
      setIssuances(issuanceRes.data.items || []);
      setTransfers(transferRes.data.items || []);
      setReturns(returnsRes.data.items || []);
      setAdjustments(adjustmentsRes.data.items || []);
      setEmployees(empRes.data.items || []);
    } catch { toast.error("Failed to load inventory"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>Inventory Management</h1>
          <p className="text-slate-500 text-sm">{warehouses.length} warehouses · {items.length} items tracked</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canCreateAdjustment && (
            <button
              onClick={() => setShowAdjustmentModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-sm font-bold text-sm hover:bg-amber-600 shadow-lg shadow-amber-500/20"
              data-testid="create-adjustment-btn"
            >
              <AlertOctagon size={18} />
              Adjust Stock
            </button>
          )}
          {canCreateReturn && (
            <button
              onClick={() => setShowReturnModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-sm font-bold text-sm hover:bg-red-600 shadow-lg shadow-red-500/20"
              data-testid="create-return-btn"
            >
              <Undo2 size={18} />
              Process Return
            </button>
          )}
          {canCreateTransfer && (
            <button
              onClick={() => setShowTransferModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-purple-500 text-white rounded-sm font-bold text-sm hover:bg-purple-600 shadow-lg shadow-purple-500/20"
              data-testid="create-transfer-btn"
            >
              <ArrowLeftRight size={18} />
              Transfer
            </button>
          )}
          {canCreateIssuance && (
            <button
              onClick={() => setShowIssuanceModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-sm font-bold text-sm hover:bg-blue-600 shadow-lg shadow-blue-500/20"
              data-testid="create-issuance-btn"
            >
              <Send size={18} />
              Issue Items
            </button>
          )}
          {canCreateGRN && (
            <button
              onClick={() => setShowGRNModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-sm font-bold text-sm hover:bg-orange-600 shadow-lg shadow-orange-500/20"
              data-testid="create-grn-btn"
            >
              <Plus size={18} />
              Create GRN
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-sm p-1 w-fit overflow-x-auto">
        {[
          { id: "summary", label: "Stock Summary", icon: BarChart2 },
          { id: "warehouses", label: "Warehouses", icon: Warehouse },
          { id: "items", label: "Items", icon: Package },
          { id: "adjustments", label: "Adjustments", icon: AlertOctagon },
          { id: "returns", label: "Returns", icon: Undo2 },
          { id: "transfers", label: "Transfers", icon: ArrowLeftRight },
          { id: "issuances", label: "Issuances", icon: Send },
          { id: "grn", label: "GRN Register", icon: FileText },
          { id: "reports", label: "Reports", icon: ClipboardList },
        ].map(tab => (
          <button 
            key={tab.id} 
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors whitespace-nowrap ${
              activeTab === tab.id ? "bg-white text-orange-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`inventory-tab-${tab.id}`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Stock Summary Tab */}
      {activeTab === "summary" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {loading ? [...Array(8)].map((_, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-sm p-4 animate-pulse">
              <div className="h-4 bg-slate-200 rounded mb-2"></div>
              <div className="h-8 bg-slate-200 rounded w-1/2"></div>
            </div>
          )) : summary.length > 0 ? summary.map(item => (
            <div key={item._id} className="bg-white border border-slate-200 rounded-sm p-4 hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${ITEM_COLORS[item._id] || "bg-slate-100 text-slate-600"}`}>
                  {item._id?.replace("_", " ")}
                </span>
                <Package size={16} className="text-slate-300" />
              </div>
              <div className="text-3xl font-black text-slate-900">{item.in_stock}</div>
              <div className="text-[10px] text-slate-400 mt-1">
                In stock · {item.issued} issued · {item.installed} installed
              </div>
              <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-400" style={{ width: `${item.total > 0 ? (item.in_stock / item.total) * 100 : 0}%` }}></div>
              </div>
            </div>
          )) : (
            <div className="col-span-4 bg-white border border-dashed border-slate-300 rounded-sm p-12 text-center">
              <Package size={48} className="mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 font-medium">No inventory items found</p>
              <p className="text-slate-400 text-sm mt-1">Create a GRN to add stock to the system</p>
              {canCreateGRN && (
                <button
                  onClick={() => setShowGRNModal(true)}
                  className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600"
                >
                  Create First GRN
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Warehouses Tab */}
      {activeTab === "warehouses" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {warehouses.length === 0 ? (
            <div className="col-span-3 text-center py-12 text-slate-400">
              <Warehouse size={32} className="mx-auto mb-2" />
              No warehouses found
            </div>
          ) : warehouses.map(wh => (
            <div key={wh.id} className="bg-white border border-slate-200 rounded-sm p-4 hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-2">
                <Warehouse size={16} className="text-orange-500" />
                <span className="font-mono text-xs font-bold text-slate-700">{wh.code}</span>
              </div>
              <div className="text-sm font-semibold text-slate-900">{wh.name}</div>
              <div className="text-xs text-slate-500 mt-1">{wh.circle_name} · {wh.warehouse_type}</div>
              <div className="text-xs text-slate-400 mt-1">{wh.address || "No address"}</div>
              <div className={`mt-2 text-[10px] font-bold uppercase ${wh.status === "active" ? "text-green-600" : "text-red-500"}`}>
                {wh.status}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Items Tab */}
      {activeTab === "items" && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
          <table className="dt-table">
            <thead>
              <tr>
                <th>Serial #</th>
                <th>Type</th>
                <th>Make</th>
                <th>Model</th>
                <th>Status</th>
                <th>Warehouse</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400">
                    <Package size={32} className="mx-auto mb-2" />
                    No items found. Create a GRN to add inventory.
                  </td>
                </tr>
              ) : items.map(item => (
                <tr key={item.id}>
                  <td><span className="font-mono text-xs font-semibold">{item.serial_number || "—"}</span></td>
                  <td>
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${ITEM_COLORS[item.item_type] || "bg-slate-100 text-slate-600"}`}>
                      {item.item_type?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="text-xs text-slate-600">{item.make || "—"}</td>
                  <td className="text-xs text-slate-600">{item.model || "—"}</td>
                  <td>
                    <span className={`text-[10px] font-bold uppercase ${
                      item.status === "in_stock" ? "text-green-600" : 
                      item.status === "issued" ? "text-orange-600" : 
                      item.status === "installed" ? "text-blue-600" : "text-slate-500"
                    }`}>
                      {item.status?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="text-xs text-slate-500">{item.current_warehouse_id?.slice(-8) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Returns Tab */}
      {activeTab === "returns" && (
        <div className="space-y-4">
          {canCreateReturn && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowReturnModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-sm font-bold text-sm hover:bg-red-600"
                data-testid="new-return-btn"
              >
                <Plus size={16} />
                New Return
              </button>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
            <table className="dt-table">
              <thead>
                <tr>
                  <th>Return #</th>
                  <th>Date</th>
                  <th>Returned By</th>
                  <th>Warehouse</th>
                  <th>Items</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {returns.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-400">
                      <Undo2 size={32} className="mx-auto mb-2" />
                      No return records found
                      {canCreateReturn && (
                        <p className="mt-2 text-sm">Click "New Return" to process a return</p>
                      )}
                    </td>
                  </tr>
                ) : returns.map(ret => (
                  <tr key={ret.id} data-testid={`return-row-${ret.id}`}>
                    <td><span className="font-mono text-xs font-bold text-red-600">{ret.doc_number}</span></td>
                    <td className="text-xs text-slate-600">{new Date(ret.created_at).toLocaleDateString("en-IN")}</td>
                    <td className="text-xs text-slate-700">{ret.returned_by_name || "—"}</td>
                    <td>
                      <span className="font-mono text-xs text-slate-600">{ret.warehouse_code || ret.to_warehouse_id?.slice(-8) || "—"}</span>
                    </td>
                    <td className="text-xs font-semibold text-slate-700">{ret.total_items || ret.items?.length || 0}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${
                        ret.status === "completed" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {ret.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Adjustments Tab */}
      {activeTab === "adjustments" && (
        <div className="space-y-4">
          {canCreateAdjustment && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowAdjustmentModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-sm font-bold text-sm hover:bg-amber-600"
                data-testid="new-adjustment-btn"
              >
                <Plus size={16} />
                New Adjustment
              </button>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
            <table className="dt-table">
              <thead>
                <tr>
                  <th>Adjustment #</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Warehouse</th>
                  <th>Items</th>
                  <th>Reference</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-slate-400">
                      <AlertOctagon size={32} className="mx-auto mb-2" />
                      No adjustment records found
                      {canCreateAdjustment && (
                        <p className="mt-2 text-sm">Click "New Adjustment" to reconcile stock</p>
                      )}
                    </td>
                  </tr>
                ) : adjustments.map(adj => (
                  <tr key={adj.id} data-testid={`adjustment-row-${adj.id}`}>
                    <td><span className="font-mono text-xs font-bold text-amber-600">{adj.doc_number}</span></td>
                    <td className="text-xs text-slate-600">{new Date(adj.created_at).toLocaleDateString("en-IN")}</td>
                    <td>
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 capitalize">
                        {adj.adjustment_type?.replace("_", " ")}
                      </span>
                    </td>
                    <td>
                      <span className="font-mono text-xs text-slate-600">{adj.warehouse_code || "—"}</span>
                    </td>
                    <td className="text-xs font-semibold text-slate-700">{adj.total_items || adj.items?.length || 0}</td>
                    <td className="text-xs text-slate-500">{adj.reference_doc || "—"}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${
                        adj.status === "completed" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {adj.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* GRN Register Tab */}
      {activeTab === "grn" && (
        <div className="space-y-4">
          {canCreateGRN && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowGRNModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-sm font-bold text-sm hover:bg-green-600"
              >
                <Plus size={16} />
                New GRN
              </button>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
            <table className="dt-table">
              <thead>
                <tr>
                  <th>GRN Number</th>
                  <th>Date</th>
                  <th>Supplier</th>
                  <th>Invoice #</th>
                  <th>Items</th>
                  <th>Value</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {grns.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-slate-400">
                      <FileText size={32} className="mx-auto mb-2" />
                      No GRN records found
                      {canCreateGRN && (
                        <p className="mt-2 text-sm">Click "New GRN" to create the first goods receipt</p>
                      )}
                    </td>
                  </tr>
                ) : grns.map(grn => (
                  <tr key={grn.id} className="cursor-pointer hover:bg-orange-50" onClick={() => setSelectedGRN(grn)}>
                    <td><span className="font-mono text-xs font-bold text-orange-600">{grn.doc_number}</span></td>
                    <td className="text-xs text-slate-600">
                      {grn.created_at ? new Date(grn.created_at).toLocaleDateString("en-IN") : "—"}
                    </td>
                    <td className="text-sm text-slate-700 font-medium">{grn.supplier || "—"}</td>
                    <td className="text-xs text-slate-500 font-mono">{grn.supplier_invoice || "—"}</td>
                    <td className="text-xs font-bold text-slate-700">{grn.total_items}</td>
                    <td className="text-xs font-bold text-green-600">₹{(grn.total_value || 0).toLocaleString("en-IN")}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        grn.status === "completed" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {grn.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transfers Tab */}
      {activeTab === "transfers" && (
        <div className="space-y-4">
          {canCreateTransfer && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowTransferModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-sm font-bold text-sm hover:bg-purple-600"
              >
                <ArrowLeftRight size={16} />
                New Transfer
              </button>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
            <table className="dt-table">
              <thead>
                <tr>
                  <th>Transfer #</th>
                  <th>Date</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Items</th>
                  <th>Reason</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {transfers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-slate-400">
                      <ArrowLeftRight size={32} className="mx-auto mb-2" />
                      No transfer records found
                      {canCreateTransfer && (
                        <p className="mt-2 text-sm">Click "New Transfer" to move items between warehouses</p>
                      )}
                    </td>
                  </tr>
                ) : transfers.map(tr => (
                  <tr key={tr.id}>
                    <td><span className="font-mono text-xs font-bold text-purple-600">{tr.doc_number}</span></td>
                    <td className="text-xs text-slate-600">
                      {tr.created_at ? new Date(tr.created_at).toLocaleDateString("en-IN") : "—"}
                    </td>
                    <td>
                      <div>
                        <span className="font-mono text-xs font-bold text-slate-700">{tr.from_warehouse_code}</span>
                        <p className="text-[10px] text-slate-400">{tr.from_warehouse_name}</p>
                      </div>
                    </td>
                    <td>
                      <div>
                        <span className="font-mono text-xs font-bold text-slate-700">{tr.to_warehouse_code}</span>
                        <p className="text-[10px] text-slate-400">{tr.to_warehouse_name}</p>
                      </div>
                    </td>
                    <td className="text-xs font-bold text-slate-700">{tr.total_items}</td>
                    <td className="text-xs text-slate-600 capitalize">{tr.reason?.replace("_", " ") || "—"}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        tr.status === "completed" ? "bg-green-100 text-green-700" : 
                        tr.status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        {tr.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Issuances Tab */}
      {activeTab === "issuances" && (
        <div className="space-y-4">
          {canCreateIssuance && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowIssuanceModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-sm font-bold text-sm hover:bg-blue-600"
              >
                <Send size={16} />
                New Issuance
              </button>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
            <table className="dt-table">
              <thead>
                <tr>
                  <th>Issuance #</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Issued To</th>
                  <th>Items</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {issuances.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-400">
                      <Send size={32} className="mx-auto mb-2" />
                      No issuance records found
                      {canCreateIssuance && (
                        <p className="mt-2 text-sm">Click "New Issuance" to issue items to field staff</p>
                      )}
                    </td>
                  </tr>
                ) : issuances.map(iss => (
                  <tr key={iss.id}>
                    <td><span className="font-mono text-xs font-bold text-blue-600">{iss.doc_number || iss.id?.slice(-8)}</span></td>
                    <td className="text-xs text-slate-600">
                      {iss.created_at ? new Date(iss.created_at).toLocaleDateString("en-IN") : "—"}
                    </td>
                    <td>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        iss.issuance_type === "bulk" ? "bg-orange-100 text-orange-700" : "bg-purple-100 text-purple-700"
                      }`}>
                        {iss.issuance_type}
                      </span>
                    </td>
                    <td className="text-sm text-slate-700">{iss.issued_to_name || "—"}</td>
                    <td className="text-xs font-bold text-slate-700">{iss.items?.length || 0}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        iss.status === "completed" ? "bg-green-100 text-green-700" : 
                        iss.status === "partial" ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        {iss.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* GRN Creation Modal */}
      {showGRNModal && (
        <CreateGRNModal
          warehouses={warehouses}
          onClose={() => setShowGRNModal(false)}
          onCreated={() => {
            setShowGRNModal(false);
            fetchData();
            setActiveTab("grn");
          }}
        />
      )}

      {/* GRN Detail Modal */}
      {selectedGRN && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl animate-fade-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{selectedGRN.doc_number}</h2>
                <p className="text-slate-500 text-xs">GRN Details</p>
              </div>
              <button onClick={() => setSelectedGRN(null)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Supplier:</span> <span className="font-medium">{selectedGRN.supplier}</span></div>
                <div><span className="text-slate-500">Invoice:</span> <span className="font-mono">{selectedGRN.supplier_invoice || "—"}</span></div>
                <div><span className="text-slate-500">Date:</span> <span>{new Date(selectedGRN.created_at).toLocaleDateString("en-IN")}</span></div>
                <div><span className="text-slate-500">Status:</span> <span className="font-bold text-green-600">{selectedGRN.status}</span></div>
              </div>
              
              <div className="border-t border-slate-200 pt-4">
                <h4 className="font-bold text-slate-700 text-sm mb-2">Items ({selectedGRN.items?.length || 0})</h4>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 text-xs text-slate-500">Type</th>
                        <th className="text-left p-2 text-xs text-slate-500">Serial</th>
                        <th className="text-left p-2 text-xs text-slate-500">Make</th>
                        <th className="text-right p-2 text-xs text-slate-500">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedGRN.items?.map((item, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="p-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ITEM_COLORS[item.item_type] || "bg-slate-100"}`}>
                              {item.item_type}
                            </span>
                          </td>
                          <td className="p-2 font-mono text-xs">{item.serial_number || "—"}</td>
                          <td className="p-2 text-xs">{item.make || "—"}</td>
                          <td className="p-2 text-right text-xs">{item.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-slate-100 rounded-sm p-3 flex justify-between items-center">
                <span className="text-slate-600">Total Items: <strong>{selectedGRN.total_items}</strong></span>
                <span className="text-green-600 font-bold">Total Value: ₹{(selectedGRN.total_value || 0).toLocaleString("en-IN")}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === "reports" && (
        <ReportsTab warehouses={warehouses} />
      )}

      {/* Issuance Creation Modal */}
      {showIssuanceModal && (
        <CreateIssuanceModal
          availableItems={items}
          employees={employees}
          onClose={() => setShowIssuanceModal(false)}
          onCreated={() => {
            setShowIssuanceModal(false);
            fetchData();
            setActiveTab("issuances");
          }}
        />
      )}

      {/* Transfer Creation Modal */}
      {showTransferModal && (
        <CreateTransferModal
          warehouses={warehouses}
          items={items}
          onClose={() => setShowTransferModal(false)}
          onCreated={() => {
            setShowTransferModal(false);
            fetchData();
            setActiveTab("transfers");
          }}
        />
      )}

      {/* Return Creation Modal */}
      {showReturnModal && (
        <CreateReturnModal
          warehouses={warehouses}
          employees={employees}
          onClose={() => setShowReturnModal(false)}
          onCreated={() => {
            setShowReturnModal(false);
            fetchData();
            setActiveTab("returns");
          }}
        />
      )}

      {/* Adjustment Creation Modal */}
      {showAdjustmentModal && (
        <CreateAdjustmentModal
          warehouses={warehouses}
          items={items}
          onClose={() => setShowAdjustmentModal(false)}
          onCreated={() => {
            setShowAdjustmentModal(false);
            fetchData();
            setActiveTab("adjustments");
          }}
        />
      )}
    </div>
  );
}
